/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    Button,
    ITelemetryContent,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import classnames from 'classnames';

/**
 * Class names used in component.
 */
const componentClassName: string = 'msc-refiner-apply';

/**
 * IRefinerApplyComponent interface.
 */
interface IRefinerApplyComponent extends IComponent<IRefinerApplyProps> {}

/**
 * IRefinerApplyProps interface.
 */
export interface IRefinerApplyProps extends IComponentProps<{}> {
    className?: string;
    text?: string;
    ariaLabel?: string;
    isDisabled?: boolean;
    telemetryContent?: ITelemetryContent;
    onClick?(): void;
}

/**
 * RefinerApply component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerApplyFunctionalComponent: React.FC<IRefinerApplyProps> = (props: IRefinerApplyProps) => {
    const { className, text, ariaLabel, isDisabled, telemetryContent, onClick } = props;

    // define telemetry attributes
    const applyRefinerAttributes = getTelemetryAttributes(
        props.telemetryContent!,
        getPayloadObject('click', telemetryContent!, "Apply Refiner")
    );

    return (
        <Button
            className={classnames(className, componentClassName)}
            disabled={isDisabled}
            aria-label={ariaLabel ?? text}
            aria-disabled={isDisabled}
            onClick={onClick}
            {...applyRefinerAttributes}
        >
            {text}
        </Button>
    );
};

/**
 * RefinerApply component.
 */
export const RefinerApplyComponent: React.FunctionComponent<IRefinerApplyProps> = msdyn365Commerce.createComponent<IRefinerApplyComponent>(
    'Refiner-Apply',
    {
        component: React.memo(RefinerApplyFunctionalComponent)
    }
);
